import React, { useMemo, useState } from 'react';
import {
  Grid,
  Stack,
  TextField,
  MenuItem,
  Typography,
  Box,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import {
  PageHeader,
  SearchFilterBar,
  DataTable,
  StatusChip,
  FormDialog,
  ProductSearchInput,
  BarcodeScannerModal,
  flattenPages,
} from '../components';
import {
  useAdjustmentsPaginated,
  useCreateAdjustment,
} from '../hooks/useStockAdjustments';
import { useBusinessUuid } from '../useBusinessUuid';
import { colors, formatDate, humanize, ADJUSTMENT_REASONS } from '../constants';

const emptyForm = {
  sku: '',
  productUuid: '',
  productType: 'product',
  adjustmentType: 'decrease',
  quantity: '',
  reason: 'count_correction',
  notes: '',
};

export default function StockAdjustments() {
  const businessUuid = useBusinessUuid();
  const [search, setSearch] = useState('');
  const [reasonFilter, setReasonFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [selectedItem, setSelectedItem] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [error, setError] = useState(null);

  const params = useMemo(
    () => ({
      limit: 20,
      reason: reasonFilter || undefined,
      search: search || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    }),
    [reasonFilter, search, dateFrom, dateTo]
  );

  const query = useAdjustmentsPaginated(params);
  const createAdj = useCreateAdjustment();
  const items = flattenPages(query);

  const openCreate = () => {
    setForm(emptyForm);
    setSelectedItem(null);
    setError(null);
    setOpen(true);
  };

  const handleItemSelected = (item) => {
    setForm((f) => ({
      ...f,
      sku: item.sku,
      productUuid: item.uuid,
      productType: item.type,
    }));
    setSelectedItem(item);
    setError(null);
  };

  const handleSubmit = async () => {
    setError(null);
    if (!form.sku.trim()) {
      setError('Please select or enter a product SKU.');
      return;
    }
    const qty = parseFloat(form.quantity);
    if (!Number.isFinite(qty) || qty <= 0) {
      setError('Quantity must be greater than zero.');
      return;
    }
    try {
      await createAdj.mutateAsync({
        businessUuid,
        productUuid: form.productUuid || form.sku,
        productType: form.productType,
        adjustmentType: form.adjustmentType,
        quantity: qty,
        reason: form.reason,
        notes: form.notes || undefined,
        adjustedBy: 'partner',
      });
      setOpen(false);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Something went wrong');
    }
  };

  const projected =
    selectedItem && form.quantity
      ? form.adjustmentType === 'increase'
        ? selectedItem.stock + (parseFloat(form.quantity) || 0)
        : Math.max(0, selectedItem.stock - (parseFloat(form.quantity) || 0))
      : null;

  const columns = [
    {
      key: 'sku',
      label: 'Product',
      render: (row) => (
        <Stack direction="row" spacing={1} alignItems="center">
          {row.adjustmentType === 'increase' ? (
            <ArrowUpwardIcon sx={{ color: colors.success, fontSize: 18 }} />
          ) : (
            <ArrowDownwardIcon sx={{ color: colors.error, fontSize: 18 }} />
          )}
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.sku}</Typography>
        </Stack>
      ),
    },
    { key: 'reason', label: 'Reason', render: (row) => <StatusChip status={row.reason} /> },
    { key: 'adjustedBy', label: 'By' },
    {
      key: 'quantity',
      label: 'Qty',
      align: 'right',
      render: (row) => (
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, color: row.adjustmentType === 'increase' ? colors.success : colors.error }}
        >
          {row.adjustmentType === 'increase' ? '+' : '-'}
          {row.quantity}
        </Typography>
      ),
    },
    { key: 'createdAt', label: 'Date', render: (row) => formatDate(row.createdAt) },
  ];

  return (
    <>
      <PageHeader
        title="Adjustments"
        actionLabel="New Adjustment"
        actionIcon={<AddIcon />}
        onAction={openCreate}
      />

      <SearchFilterBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search adjustments..."
        filters={[
          {
            label: 'Reason',
            value: reasonFilter,
            onChange: setReasonFilter,
            options: ADJUSTMENT_REASONS,
          },
        ]}
      >
        <Stack direction="row" spacing={1}>
          <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} sx={{ backgroundColor: '#fff' }} />
          <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={dateTo} onChange={(e) => setDateTo(e.target.value)} sx={{ backgroundColor: '#fff' }} />
        </Stack>
      </SearchFilterBar>

      <DataTable
        columns={columns}
        rows={items}
        loading={query.isLoading}
        emptyTitle="No adjustments"
        hasNextPage={query.hasNextPage}
        isFetchingNextPage={query.isFetchingNextPage}
        fetchNextPage={query.fetchNextPage}
      />

      <FormDialog
        open={open}
        title="New Adjustment"
        onClose={() => setOpen(false)}
        onSubmit={handleSubmit}
        submitLabel="Create Adjustment"
        loading={createAdj.isPending}
      >
        {error && (
          <Typography variant="body2" sx={{ color: colors.error, mb: 1.5 }}>
            {error}
          </Typography>
        )}
        <ProductSearchInput
          value={form.sku}
          onChangeText={(v) => {
            setForm((f) => ({ ...f, sku: v }));
            setSelectedItem(null);
          }}
          onItemSelected={handleItemSelected}
          productType="product"
          selectedItem={selectedItem}
          onClear={() => {
            setForm((f) => ({ ...f, sku: '', productUuid: '' }));
            setSelectedItem(null);
          }}
          onBarcodeScan={() => setScannerOpen(true)}
          label="Product"
          isRequired
        />
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Adjustment"
              value={form.adjustmentType}
              onChange={(e) => setForm((f) => ({ ...f, adjustmentType: e.target.value }))}
              fullWidth
              size="small"
            >
              <MenuItem value="increase">Increase</MenuItem>
              <MenuItem value="decrease">Decrease</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Quantity"
              type="number"
              value={form.quantity}
              onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
              fullWidth
              required
              size="small"
            />
          </Grid>
          {projected !== null && (
            <Grid item xs={12}>
              <Box sx={{ p: 1.5, borderRadius: 1, backgroundColor: colors.primaryLight }}>
                <Typography variant="body2" sx={{ color: colors.primary, fontWeight: 600 }}>
                  Projected Stock: {projected} {selectedItem?.unit}
                </Typography>
              </Box>
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Reason"
              value={form.reason}
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
              fullWidth
              size="small"
            >
              {ADJUSTMENT_REASONS.map((r) => (
                <MenuItem key={r} value={r}>{humanize(r)}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Notes"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
              size="small"
            />
          </Grid>
        </Grid>
      </FormDialog>

      <BarcodeScannerModal
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onItemFound={handleItemSelected}
        onBarcodeNotFound={(code) => setError(`No product found for barcode "${code}".`)}
      />
    </>
  );
}
